/* eslint-disable @typescript-eslint/ban-types */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { EventBlock } from "@iventis/domain-model/model/eventBlock";
import { createMachine, assign } from "xstate";
import { Hierarchy } from "@iventis/domain-model/model/hierarchy";
import { findHierarchyEntityById } from "./hierarchy.helpers";
import { createEventBlock, deleteEventBlock, getEventBlockHierarchyWithEventBlockId, updateEventBlock, EventBlockPutResponse } from "./schedule-api-functions";
import { SubEventCrudOperation } from "./sub-event-form-wrapper";

type SubEventDialogMachineContext = {
    /** Create or Edit */
    crudOperation: SubEventCrudOperation;
    /** Number of entities saved (incremented using the save and add next functionality) */
    count: number;
    /** Upon submission, should we return to idle and allow user to quickly add another event block */
    willAddAnother: boolean;
    /** The parent session of the sub event in question */
    session: EventBlock;
    /** Either the current event block id if we are editing, or the previously edited/created event block if we are creating */
    previouslySavedEventBlockId: string;
    /** For edits on a sub-event, this property points to the existing event block with it's old values */
    existingEventBlock: EventBlock;
};

export type SubEventDialogMachineEvents =
    | { type: "SUBMIT"; payload: Hierarchy<EventBlock> }
    | { type: "SUBMIT_REQUEST"; payload: { willAddAnother: boolean } }
    | { type: "DELETE" }
    | { type: "INVALID" }
    | { type: "VALID" }
    | { type: "CONFIRM_DELETE"; payload: string }
    | { type: "CANCEL_DELETE" };

export const subEventDialogMachine = createMachine(
    {
        schema: {
            context: {} as SubEventDialogMachineContext,
            events: {} as SubEventDialogMachineEvents,
            services: {} as {
                submit: { data: Hierarchy<EventBlock> | EventBlockPutResponse };
                deleteEntity: { data: string };
            },
        },
        // eslint-disable-next-line no-undef
        tsTypes: {} as import("./sub-event-dialog.machine.typegen").Typegen0,
        context: { crudOperation: null, count: 0, willAddAnother: false, session: null, existingEventBlock: null, previouslySavedEventBlockId: null },
        id: "subEventMachine",
        initial: "idle",
        states: {
            idle: {
                type: "parallel",
                states: {
                    validation: {
                        initial: "invalid",
                        states: {
                            valid: {
                                on: {
                                    // Submit request will firstly set the add another state and then ask formik to submit the form
                                    SUBMIT_REQUEST: { actions: ["setWillAddAnother", "requestSubmit"] },
                                    // Triggered when formik submits the form, allowing us to transition to submitting (to the backend) state
                                    SUBMIT: {
                                        target: "#submitting",
                                    },
                                    INVALID: {
                                        target: "invalid",
                                    },
                                },
                            },
                            invalid: {
                                on: {
                                    VALID: {
                                        target: "valid",
                                    },
                                },
                            },
                        },
                    },
                    deleteConfirmation: {
                        initial: "closed",
                        states: {
                            closed: {
                                id: "closed",
                                on: { DELETE: "open" },
                            },
                            open: {
                                on: {
                                    CONFIRM_DELETE: "#deleting",
                                    CANCEL_DELETE: "closed",
                                },
                            },
                        },
                    },
                },
            },
            deleting: {
                id: "deleting",
                invoke: {
                    src: "deleteEntity",
                    onDone: {
                        target: "#finished",
                        actions: "onDelete",
                    },
                    onError: "#failure",
                },
            },
            submitting: {
                id: "submitting",
                invoke: {
                    src: "submit",
                    onDone: [
                        // If the user intends to add another event block, transition back to idle and reset the form
                        { cond: "willAddAnother", target: "idle", actions: ["onSubmit", "resetState"] },
                        // Else, complete
                        { target: "finished", actions: "onSubmit" },
                    ],
                    onError: "failure",
                },
            },
            failure: {
                id: "failure",
                type: "final",
            },
            finished: {
                id: "finished",
                type: "final",
            },
        },
    },
    {
        guards: { willAddAnother: (context) => context.willAddAnother },
        actions: {
            resetState: assign((context, event) => ({
                crudOperation: SubEventCrudOperation.CREATE,
                count: context.count + 1,
                willAddAnother: false,
                previouslySavedEventBlockId:
                    // If the previous operation was creation, we set the saved event block id to the new response, else we can just use the existing (since edit will only ever be on the first count)
                    context.crudOperation === SubEventCrudOperation.CREATE ? (event.data as Hierarchy<EventBlock>).item?.id : context.previouslySavedEventBlockId,
            })),
            setWillAddAnother: assign({ willAddAnother: (_, event) => event.payload.willAddAnother }),
        },
        services: {
            submit: async ({ crudOperation, session, existingEventBlock }, event) => {
                switch (crudOperation) {
                    case SubEventCrudOperation.CREATE: {
                        const newEventBlock = await createEventBlock(event.payload);
                        const updatedHierarchy = await getEventBlockHierarchyWithEventBlockId(session.id, session.startAt);
                        // Missing startTime and endTime so we get if from the updated hierarchy
                        const newEventBlockFromHierarchy = findHierarchyEntityById([updatedHierarchy], newEventBlock.item.id);
                        return newEventBlockFromHierarchy;
                    }
                    case SubEventCrudOperation.EDIT: {
                        const results = await updateEventBlock(existingEventBlock, event.payload, session.id);
                        return results;
                    }
                    default:
                        return null;
                }
            },
            deleteEntity: async (context, event) => {
                await deleteEventBlock(event.payload, { calendarLevel: 2 });
                return event.payload; // entity id
            },
        },
    }
);
